import * as React from "react";
import { deleteSession, getCurrentUser, User } from "../utils/api";

export type AuthStatus = "loading" | "authenticated" | "anonymous";

export type AuthContextValue = {
  user: User | null;
  status: AuthStatus;
  signOut: () => Promise<void>;
  refresh: () => Promise<User | null>;
  /**
   * Installs a user the caller already has, without a round trip. The
   * subscription endpoints (change tier, cancel) write the account's tier,
   * price, and subscription id before they respond, so the user they return is
   * authoritative — a follow-up /me could only return the same thing, or,
   * inside the de-duplication window in utils/api, something staler. Use
   * refresh when the change happened somewhere we can't see, like a webhook.
   */
  applyUser: (user: User) => void;
};

// A real default (rather than throwing) keeps SSR/prerender of any consumer
// safe: pages render their "loading" branch until the provider hydrates.
const AuthContext = React.createContext<AuthContextValue>({
  user: null,
  status: "loading",
  signOut: async () => {},
  refresh: async () => null,
  applyUser: () => {},
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = React.useState<User | null>(null);
  const [status, setStatus] = React.useState<AuthStatus>("loading");

  const load = React.useCallback(
    async (force: boolean): Promise<User | null> => {
      const current = await getCurrentUser({ force }).catch(() => null);
      setUser(current);
      setStatus(current ? "authenticated" : "anonymous");
      return current;
    },
    []
  );

  // Callers reach for refresh() after changing something (activation, a plan
  // change), so it always goes to the network; only the mount load below is
  // allowed to share a de-duplicated result.
  const refresh = React.useCallback(() => load(true), [load]);

  const applyUser = React.useCallback((next: User) => {
    setUser(next);
    setStatus("authenticated");
  }, []);

  const signOut = React.useCallback(async () => {
    await deleteSession();
    setUser(null);
    setStatus("anonymous");
  }, []);

  React.useEffect(() => {
    // One-time cleanup of the legacy localStorage pseudo-auth.
    localStorage.removeItem("verifiedEmail");
    localStorage.removeItem("apiKey");
    load(false);
  }, [load]);

  const value = React.useMemo(
    () => ({ user, status, signOut, refresh, applyUser }),
    [user, status, signOut, refresh, applyUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => React.useContext(AuthContext);
